import React from "react";
import {
  Alert,
  Box,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { getAwaitingInputSurfaceStyles } from "@/shared/utils/awaitingInputStyles";
import type { ReviewGap } from "../types";

interface ClarificationPanelProps {
  gaps: ReviewGap[];
  onResolveGap: (id: string, resolution: string) => void;
  disabled?: boolean;
}

// ── Gap Item ───────────────────────────────────────────

function GapItem({
  gap,
  onResolve,
  disabled,
}: {
  gap: ReviewGap;
  onResolve: (id: string, resolution: string) => void;
  disabled?: boolean;
}) {
  return (
    <Box
      sx={(theme) => {
        const awaitingInputStyles = getAwaitingInputSurfaceStyles(theme);
        return {
          py: 1.25,
          px: 1.5,
          borderRadius: 2,
          backgroundColor: awaitingInputStyles.backgroundColor,
          border: `1px solid ${awaitingInputStyles.borderColor}`,
        };
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <WarningAmberIcon
          fontSize="small"
          sx={(theme) => ({
            mt: 0.25,
            color: getAwaitingInputSurfaceStyles(theme).accentColor,
          })}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {gap.description}
          </Typography>
          <Chip
            label={gap.category}
            size="small"
            variant="outlined"
            sx={{ mt: 0.5, height: 20, fontSize: "0.7rem" }}
          />

          {gap.type === "multiple-options" && gap.options?.length ? (
            <RadioGroup
              value={gap.resolution ?? ""}
              onChange={(e) => onResolve(gap.id, e.target.value)}
              sx={{ mt: 1 }}
            >
              {gap.options.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio size="small" disabled={disabled} />}
                  label={<Typography variant="body2">{option}</Typography>}
                />
              ))}
            </RadioGroup>
          ) : (
            <TextField
              size="small"
              placeholder={gap.type === "missing" ? "Enter value..." : "Clarify..."}
              value={gap.resolution ?? ""}
              onChange={(e) => onResolve(gap.id, e.target.value)}
              fullWidth
              disabled={disabled}
              sx={{ mt: 1 }}
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
}

// ── Panel ──────────────────────────────────────────────

export const ClarificationPanel: React.FC<ClarificationPanelProps> = ({
  gaps,
  onResolveGap,
  disabled,
}) => {
  if (gaps.length === 0) {
    return (
      <Alert severity="success">
        The AI Assistant has no outstanding questions for this First Draft.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography
        variant="overline"
        sx={(theme) => ({
          fontWeight: 700,
          color: getAwaitingInputSurfaceStyles(theme).accentColor,
          letterSpacing: 1.5,
        })}
      >
        Awaiting Clarification ({gaps.length})
      </Typography>
      {gaps.map((gap) => (
        <GapItem
          key={gap.id}
          gap={gap}
          onResolve={onResolveGap}
          disabled={disabled}
        />
      ))}
    </Stack>
  );
};
